import React, { useEffect, useState } from "react";
import {
    Table,
    Spinner,
    Alert,
    Badge,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
} from "reactstrap";
import {
    Carousel,
    CarouselItem,
    CarouselControl,
    CarouselIndicators,
    CarouselCaption,
} from "reactstrap";
import { getPropertiesByValuation } from "../../../../services/ValitanApiService";
import "./PropertiesTab.css";


const PropertiesTab = ({ valuationId }) => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);

    // modal state
    const [selectedPhotos, setSelectedPhotos] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [animating, setAnimating] = useState(false);

    useEffect(() => {
        if (!valuationId) return;

        const fetchProperties = async () => {
            try {
                setLoading(true);
                const response = await getPropertiesByValuation(valuationId);

                if (response.status === 200 && response.data) {
                    setProperties(response.data.data || []);
                } else {
                    setProperties([]);
                }
            } catch (error) {
                console.error("Failed to fetch properties:", error);
                setProperties([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProperties();
    }, [valuationId]);

    const openPhotoModal = (photos) => {
        setSelectedPhotos(photos || []);
        setActiveIndex(0); // reset to first photo
        setModalOpen(true);
    };

    const next = () => {
        if (animating) return;
        if (activeIndex < selectedPhotos.length - 1) {
            setActiveIndex(activeIndex + 1);
        }
    };

    const previous = () => {
        if (animating) return;
        if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        }
    };

    const goToIndex = (newIndex) => {
        if (animating) return;
        setActiveIndex(newIndex);
    };

    const formatCaption = (text) => {
        if (!text) return "";
        return text
            .toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    if (loading) {
        return (
            <div className="text-center my-3">
                <Spinner color="primary" />
            </div>
        );
    }

    if (!properties.length) {
        return (
            <Alert color="info">No properties available for this valuation</Alert>
        );
    }

    return (
        <div>
            <h5 className="mb-3 text-primary">Properties</h5>
            <Table bordered hover responsive>
                <thead className="table-light">
                    <tr>
                        <th>#</th>
                        <th>Title No.</th>
                        <th>Property Type</th>
                        <th>Location</th>
                        <th>Land Size (m²)</th>
                        <th>Built-up Area (m²)</th>
                        <th>Condition</th>
                        <th>Surveyed?</th>
                        <th>Utilities?</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {properties.map((property, index) => (
                        <tr key={property.id}>
                            <td>{index + 1}</td>
                            <td>{property.title_number || "-"}</td>
                            <td>{property.property_type?.name || "-"}</td>
                            <td>
                                {property.village?.ward?.name || property.village?.name ? (
                                    <ul className="list-unstyled mb-0">
                                        {property.village?.ward?.name && (
                                            <li className="d-flex align-items-center mb-1">
                                                <i
                                                    className="ri-arrow-right-circle-line text-primary me-2"
                                                    style={{ fontSize: "16px" }}
                                                ></i>
                                                Ward: {property.village.ward.name}
                                            </li>
                                        )}
                                        {property.village?.name && (
                                            <li className="d-flex align-items-center mb-1">
                                                <i
                                                    className="ri-arrow-right-circle-line text-primary me-2"
                                                    style={{ fontSize: "16px" }}
                                                ></i>
                                                Village: {property.village.name}
                                            </li>
                                        )}
                                    </ul>
                                ) : (
                                    "-"
                                )}
                            </td>
                            <td>{property.land_size || "-"}</td>
                            <td>{property.built_up_area || "-"}</td>
                            <td>
                                <Badge color="info">
                                    {property.building_condition || "-"}
                                </Badge>
                            </td>
                            <td>
                                {property.is_surveyed ? (
                                    <Badge color="success">Yes</Badge>
                                ) : (
                                    <Badge color="secondary">No</Badge>
                                )}
                            </td>
                            <td>
                                {property.has_utilities ? (
                                    <Badge color="success">Yes</Badge>
                                ) : (
                                    <Badge color="secondary">No</Badge>
                                )}
                            </td>
                            <td>
                                {property.photos && property.photos.length > 0 && (
                                    <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() => openPhotoModal(property.photos)}
                                    >
                                        Photos
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {/* Photos Modal */}
            <Modal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                size="lg"
            >
                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    Property Photos
                </ModalHeader>
                <ModalBody>
                    {selectedPhotos && selectedPhotos.length > 0 ? (
                        <Carousel
                            activeIndex={activeIndex}
                            next={next}
                            previous={previous}
                            ride={false}
                        >
                            <CarouselIndicators
                                items={selectedPhotos}
                                activeIndex={activeIndex}
                                onClickHandler={goToIndex}
                            />

                            {selectedPhotos.map((photo, idx) => (
                                <CarouselItem
                                    key={photo.id}
                                    onExiting={() => setAnimating(true)}
                                    onExited={() => setAnimating(false)}
                                >
                                    <img
                                        src={photo.photo_url}
                                        alt={photo.photo_type}
                                        className="d-block w-100 img-fluid"
                                        style={{ objectFit: "cover", height: "500px" }}
                                    />
                                    <CarouselCaption
                                        captionHeader={formatCaption(photo.photo_type)}
                                        headerClassName="text-white fw-bold"
                                        className="custom-caption"
                                    />
                                </CarouselItem>
                            ))}
                            

                            {activeIndex > 0 && (
                                <CarouselControl
                                    direction="prev"
                                    directionText="Previous"
                                    onClickHandler={previous}
                                />
                            )}
                            {activeIndex < selectedPhotos.length - 1 && (
                                <CarouselControl
                                    direction="next"
                                    directionText="Next"
                                    onClickHandler={next}
                                />
                            )}
                        </Carousel>
                    ) : (
                        <Alert color="info">
                            No photos available for this property
                        </Alert>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default PropertiesTab;
